import { Head, Link, router } from "@inertiajs/react"
import { FileText, Clock, CheckCircle, AlertCircle, Plus, Video, ArrowRight, Users } from "lucide-react"
import { useState } from "react"
import { CoachLayout } from "./components/CoachLayout"
import { CreateGroupDialog } from "./components/CreateGroupDialog"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { NewArticleChooserDialog } from "@/components/NewArticleChooserDialog"
import { UploadTranscriptDialog } from "@/components/UploadTranscriptDialog"

const statusConfig = {
  draft: { label: "Draft", variant: "secondary", icon: FileText },
  pending_review: { label: "Pending", variant: "warning", icon: Clock },
  published: { label: "Published", variant: "success", icon: CheckCircle },
  rejected: { label: "Rejected", variant: "destructive", icon: AlertCircle },
}

function StatusBadge({ status }) {
  const config = statusConfig[status] || statusConfig.draft
  return (
    <Badge
      variant={config.variant}
      className={
        config.variant === "warning"
          ? "bg-amber-100 text-amber-800 hover:bg-amber-100"
          : config.variant === "success"
          ? "bg-green-100 text-green-800 hover:bg-green-100"
          : ""
      }
    >
      {config.label}
    </Badge>
  )
}

function formatDate(dateString) {
  if (!dateString) return "-"
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

export default function Dashboard({ currentUser, stats, recentArticles, meetingStats }) {
  const [dialogMode, setDialogMode] = useState(null) // null | "chooser" | "upload"
  const [createGroupOpen, setCreateGroupOpen] = useState(false)
  const hasMeetingActivity = (meetingStats?.active || 0) > 0 || (meetingStats?.recentCompleted || 0) > 0

  // A coach/admin who coaches no group is otherwise dead-ended -- User
  // Management, Content, and every other CoachGroupScoped page redirects
  // them right back here. Offer the way out instead of a silent bounce.
  // Author-only accounts (isCoach/isAdmin both false) get no create surface.
  const showCreateFirstGroup =
    (currentUser?.isCoach || currentUser?.isAdmin) && !currentUser?.isCoachOfAnyGroup

  return (
    <CoachLayout currentUser={currentUser}>
      <Head title="Coach Dashboard - Kingdom Factor" />

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Dashboard</h1>
            <p className="text-zinc-600">
              Welcome back, {currentUser?.name || "Coach"}
            </p>
          </div>
          <Button variant="gradient" data-tour="new-article-btn" onClick={() => setDialogMode("chooser")}>
            <Plus className="h-4 w-4" />
            New Article
          </Button>
        </div>

        {/* Create your first group */}
        {showCreateFirstGroup && (
          <Card className="border-kf-red/20 bg-kf-red/5">
            <CardContent className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 py-6">
              <div className="flex items-start gap-3">
                <Users className="h-8 w-8 text-kf-red shrink-0" />
                <div>
                  <h2 className="font-semibold text-zinc-900">Create your first group</h2>
                  <p className="text-sm text-zinc-600">
                    You'll need a group before you can manage users or assign content.
                  </p>
                </div>
              </div>
              <Button variant="gradient" onClick={() => setCreateGroupOpen(true)}>
                <Plus className="h-4 w-4" />
                Create Group
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div data-tour="stats-cards" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-zinc-600">
                Total Articles
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats?.total || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-zinc-600">
                Drafts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-zinc-500">
                {stats?.drafts || 0}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-zinc-600">
                Pending Review
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-amber-600">
                {stats?.pendingReview || 0}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-zinc-600">
                Published
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">
                {stats?.published || 0}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Meeting Activity */}
        {hasMeetingActivity && (
          <Card data-tour="meeting-activity">
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-2">
                <Video className="h-5 w-5 text-kf-red" />
                <CardTitle>Meeting Activity</CardTitle>
              </div>
              <Link href="/app/coach/meetings">
                <Button variant="ghost" size="sm">
                  View All
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              <div className="flex gap-6">
                {(meetingStats?.active || 0) > 0 && (
                  <div>
                    <p className="text-2xl font-bold text-amber-600">{meetingStats.active}</p>
                    <p className="text-sm text-zinc-500">Active recording{meetingStats.active !== 1 ? "s" : ""}</p>
                  </div>
                )}
                {(meetingStats?.recentCompleted || 0) > 0 && (
                  <div>
                    <p className="text-2xl font-bold text-green-600">{meetingStats.recentCompleted}</p>
                    <p className="text-sm text-zinc-500">Completed (30 days)</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Articles */}
        <Card data-tour="recent-articles">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Recent Articles</CardTitle>
            <Link href="/app/coach/articles">
              <Button variant="ghost" size="sm">
                View All
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recentArticles?.length > 0 ? (
              <div className="divide-y divide-zinc-100">
                {recentArticles.map((article) => (
                  <Link
                    key={article.id}
                    href={`/app/coach/articles/${article.id}`}
                    className="flex items-center justify-between py-3 hover:bg-zinc-50 -mx-4 px-4 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-zinc-900 truncate">
                        {article.title}
                      </p>
                      <p className="text-sm text-zinc-500">
                        {article.category || "No category"} &middot; Updated{" "}
                        {formatDate(article.updatedAt)}
                      </p>
                    </div>
                    <StatusBadge status={article.status} />
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-zinc-500">
                <FileText className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
                <p>No articles yet</p>
                <p className="text-sm">Create your first article to get started</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <NewArticleChooserDialog
        open={dialogMode === "chooser"}
        onOpenChange={(open) => { if (!open) setDialogMode((m) => m === "chooser" ? null : m) }}
        onStartScratch={() => {
          setDialogMode(null)
          router.post("/app/coach/articles")
        }}
        onUploadTranscript={() => setDialogMode("upload")}
      />
      <UploadTranscriptDialog
        open={dialogMode === "upload"}
        onOpenChange={(open) => { if (!open) setDialogMode(null) }}
      />
      <CreateGroupDialog open={createGroupOpen} onOpenChange={setCreateGroupOpen} />
    </CoachLayout>
  )
}
